/**
 * Shared Plotly layout helpers, extracted from the old ChartRenderer.
 *
 * `makeLayout` builds the base layout every chart shares (axis titles, grid,
 * legend, margins) from the normalized model + injected RenderContext. Per-type
 * overrides come from each chart's optional `buildLayout` and are merged over
 * this base by the renderer. `resolveLimitsShapes` turns `limits` into the
 * Plotly `shapes` array (a band rect + upper/lower reference lines).
 */

import type { NormalizedChartModel, ChartLimits, ChartEncoding, ChartLabels } from '../model';
import type { DataPoint, DataSeries } from '../../types/dashboard';
import type { RenderContext, ChartTrace } from '../types';
import { toFiniteNumber } from './number';
import { wrapToWidth, measureTextWidth, type FontSpec } from './text';
import { uniqueValues } from './uniqueValues';
import { referenceLineShape } from './references';
import { SEMANTIC } from '../theme/palette';
import { figureScale, scaleFont, scalePx } from '../theme/scale';
import {
  CHART_FONT,
  TITLE_FONT,
  TITLE_PAD_TOP,
  TITLE_SIDE_PAD,
  TITLE_LINE_HEIGHT,
  TITLE_MAX_HEIGHT_FRACTION,
  SUBTITLE_FONT,
  AXIS_TITLE_FONT,
  AXIS_TITLE_STANDOFF,
  SURFACE_COLOR,
  GRID_COLOR,
  GRID_WIDTH,
  AXIS_LINE_COLOR,
  AXIS_LINE_WIDTH,
  TICK_LEN,
  TICK_WIDTH,
  LIMIT_BAND_OPACITY,
  LEGEND_STYLE,
  LEGEND_PAD_BOTTOM,
  MARGIN,
  Y_HEADROOM,
  Y2_FIT_DATA_BOTTOM_HEADROOM,
  EQUAL_ASPECT_PAD,
  LEGEND_GUTTER,
  Y_HEADROOM_INSIDE_BOX,
  STATS_GUTTER,
  NUMBER_TICKS,
  HOVER_FONT,
} from '../theme/layout';

/**
 * Stroke width (px, at the reference figure width) of a limit / reference line.
 * Matches `referenceLineShape`'s own default; named here because it is scaled.
 */
const REFERENCE_LINE_WIDTH = 2;

/** Bold axis title, kept off the tick values via standoff. Shared by all axes. */
function axisTitle(text: string, scale: number): Record<string, unknown> {
  return {
    text,
    font: scaleFont(AXIS_TITLE_FONT, scale),
    standoff: scalePx(AXIS_TITLE_STANDOFF, scale),
  };
}

/** Black axis spine + outside ticks (matplotlib-style). Shared by every axis. */
function axisSpine(scale: number) {
  return {
    showline: true,
    linecolor: AXIS_LINE_COLOR,
    linewidth: AXIS_LINE_WIDTH,
    ticks: 'outside' as const,
    ticklen: scalePx(TICK_LEN, scale),
    tickwidth: TICK_WIDTH,
    tickcolor: AXIS_LINE_COLOR,
  };
}

/**
 * The subtitle as drawn: the authored text wrapped to the figure, with the shown bench
 * on a line of its own beneath it. Split rather than joined — see `ChartLabels.benchLabel`.
 */
function resolveSubtitle(
  labels: ChartLabels,
  font: FontSpec,
  fit: (text: string, font: FontSpec) => string
): string | undefined {
  const authored = labels.subtitle ? fit(labels.subtitle, font) : undefined;
  if (!labels.benchLabel) return authored;
  return authored ? `${authored}<br>${labels.benchLabel}` : labels.benchLabel;
}

/**
 * The title block: the text as it will actually be drawn (wrapped to the measured
 * width) plus the vertical space it occupies, in px.
 *
 * Exported because `title.automargin` makes that height part of the TOP MARGIN at
 * render time, and Plotly resolves it internally — so a caller deriving the paper
 * box from `layout.margin.t` alone (which is only the authored base) overstates
 * the room by every line the title wrapped into. ChartRenderer needs it to size
 * the stats box against the height that will really exist.
 */
export function resolveTitleBlock(
  model: NormalizedChartModel,
  ctx: RenderContext
): { text: string; subtitle?: string; padTop: number; height: number; scale: number } {
  const { labels, options } = model;
  const scale = figureScale(ctx.figureWidth);
  // Plotly wraps neither the title nor the subtitle — a long one overflows the
  // figure and the SVG clips it (measured on the accuracy map: 22px off the title,
  // 93px off the subtitle, cut on BOTH sides since it is centered). Break them to
  // the measured width instead. No-op until the container has been measured, and
  // opt-out per widget via `options.autoFitText`.
  const autoFitText = options?.autoFitText ?? true;
  const titleFont = scaleFont(TITLE_FONT, scale);
  // The header is anchored to the PLOT's left edge (see `title.x` in makeLayout),
  // so what it has to work with is the figure minus that offset — measured when
  // known, the authored left margin until the first render reports it. Only the
  // left is subtracted: the text may run into the right margin without being
  // clipped, since clipping happens at the figure's edge, not the plot's.
  const plotLeft = ctx.plotLeft ?? scalePx(MARGIN.base.l, scale);
  const textWidth = autoFitText
    ? (ctx.figureWidth ?? 0) - plotLeft - scalePx(TITLE_SIDE_PAD, scale)
    : 0;
  const fitTitle = (text: string, font: FontSpec) => wrapToWidth(text, textWidth, font);
  const text = fitTitle(labels.title ?? '', titleFont);
  const lines = text ? text.split('<br>').length : 0;
  // Plotly anchors the title block by its last line, so the extra lines wrapping
  // introduces grow UPWARD; one line height of pad per extra line keeps the FIRST
  // line at y ~= 25 whatever the count. Capped, because unbounded it reached 184px
  // (47% of a 388px figure) on a 9-line title and automargin then crushed the plot
  // to a strip — a collapsed plot is a worse outcome than a clipped title.
  const lineHeightPx = Math.round(titleFont.size * TITLE_LINE_HEIGHT);
  // Scales freely: the pad only has to clear the glyph ascenders, and that
  // overshoot grows with the font (see TITLE_PAD_TOP). It used to carry a
  // non-scaling floor for the modebar band; that constraint was dropped.
  const basePad = scalePx(TITLE_PAD_TOP, scale);
  // No cap until the height is measured — same "don't guess" rule as the width.
  const padCeiling =
    (ctx.figureHeight ?? 0) > 0
      ? Math.round(Math.max(basePad, ctx.figureHeight! * TITLE_MAX_HEIGHT_FRACTION))
      : Infinity;
  const padTop = Math.min(basePad + Math.max(0, lines - 1) * lineHeightPx, padCeiling);
  return {
    text,
    // The authored subtitle is wrapped; the bench is appended AFTER, on its own line, so
    // the wrap point never sees it. See `ChartLabels.benchLabel` for what that buys.
    subtitle: resolveSubtitle(labels, scaleFont(SUBTITLE_FONT, scale), fitTitle),
    padTop,
    // What `automargin` will reserve at the top: the pad plus the stack of lines.
    height: text ? padTop + lines * lineHeightPx : 0,
    scale,
  };
}

/**
 * Compute a [min, max] range from the max of *all* series/rows on this side
 * (Math.max over every value), with `headroom` extra space above the data so the
 * topmost point clears the plot's top border. Pass `headroom: 0` to fit the data
 * tightly. Bars/lines start at 0 unless data goes negative, unless `zeroFloor` is
 * false — then the low end is `dataMin` minus `bottomHeadroom * (dataMax - dataMin)`
 * (`bottomHeadroom` defaults to `headroom`; pass a distinct value to pad the bottom
 * differently, e.g. the secondary axis under `options.y2FitData`). That pad is
 * span-relative rather than value-relative on purpose: this is precisely the case
 * where the data doesn't start near 0, so padding by a fraction of `dataMin` itself
 * would give wildly different breathing room depending on how far the data sits
 * from zero, instead of a consistent fraction of the visible range. The
 * negative-data floor (`zeroFloor: true` but `dataMin < 0`) always uses `headroom`,
 * regardless of `bottomHeadroom` — that path is unrelated to the fit-data opt-in.
 * A single data point, or a series whose values are all equal, has a zero span —
 * the span-relative pad above would then be zero too, and with `headroom` also 0
 * (a chart with no legend) the range degenerates to `[v, v]`, an axis Plotly
 * cannot scale. That case falls back to a value-relative pad instead (`|| 1` as a
 * last resort when the value itself is 0), so `!zeroFloor` never returns a
 * zero-width range for a non-degenerate value.
 * `topSpanHeadroom` (default 0) adds a span-relative pad above the max as well —
 * the larger of it and `headroom` wins — for a range fit to the data whose top
 * would otherwise be the data max itself, which Plotly's border clips half the
 * stroke of. It is 0 for a zero span.
 * Returns undefined when there are no numeric Y values (leave Plotly on autorange).
 */
function axisRangeWithHeadroom(
  data: DataPoint[],
  series: DataSeries[],
  encoding: ChartEncoding,
  side: 'left' | 'right',
  headroom: number,
  extraValues: number[] = [],
  zeroFloor = true,
  bottomHeadroom: number = headroom,
  topSpanHeadroom = 0
): [number, number] | undefined {
  const keys =
    series.length > 0
      ? series
          .filter((s) => (s.yAxis === 'right' ? side === 'right' : side === 'left'))
          .map((s) => s.dataKey)
      : side === 'left' && encoding.yKey
        ? [encoding.yKey]
        : [];
  const values: number[] = [];
  for (const row of data) {
    for (const k of keys) {
      const n = toFiniteNumber(row[k]);
      if (n !== null) values.push(n);
    }
  }
  // Reference/limit lines are drawn in DATA coordinates, so a limit above the data
  // max falls outside an explicit range and Plotly clips it away — the chart then
  // reads as if every value sits under a limit the viewer cannot see. They must
  // therefore widen the range like any other value. This mattered less while the
  // headroom was 20%, which usually covered a nearby limit by accident.
  if (values.length === 0 && extraValues.length === 0) return undefined;
  values.push(...extraValues);
  const dataMax = Math.max(...values);
  const dataMin = Math.min(...values);
  const span = dataMax - dataMin;
  const max = dataMax + Math.max(Math.abs(dataMax) * headroom, span * topSpanHeadroom);
  const min = !zeroFloor
    ? span > 0
      ? dataMin - span * bottomHeadroom
      : dataMin - (Math.abs(dataMin) || 1) * bottomHeadroom
    : dataMin < 0
      ? dataMin - Math.abs(dataMin) * headroom
      : 0;
  return [min, max];
}

/**
 * Padded [min, max] extent of one column, for the plan view's explicit axis ranges.
 *
 * Returns undefined when the column has no numeric values or a zero span — an
 * explicit `[v, v]` is not a range Plotly can scale under an aspect constraint, so
 * the honest answer for a degenerate extent is to emit none and let it autorange.
 */
/**
 * Memoised per (rows array, key). The extents are asked for up to six times per render —
 * twice by `legendTakesGutter`, which `makeLayout` reaches through two paths and
 * `ChartRenderer` through a third, plus the aspect ranges below — and each ask is a full
 * scan. Measured on a 60k-block model before this: 11 of `makeLayout`'s 16 ms.
 *
 * Keyed on array IDENTITY, which is safe because these arrays are DERIVED and replaced
 * rather than mutated: `normalizeConfig` builds a new `data` on every resolve, and
 * `unfilteredData` with it. A caller that mutated rows in place would read a stale
 * extent — nothing in the kit does, and a WeakMap keeps the entry alive no longer than
 * the rows themselves.
 *
 * That guarantee is about the INPUT rows, not the OUTPUT range — every call for the same
 * (rows, key) returns a CLONE of the cached `[min, max]`, never the array actually stored.
 * The array this feeds is `layout.xaxis.range`/`layout.yaxis.range` on an `equalAspect`
 * chart, and plotly.js does not clone an explicitly-authored range on the way into
 * `_fullLayout` — a drag-zoom's `Plotly.relayout(gd, {'xaxis.range[0]': …})` mutates that
 * exact array IN PLACE. Handing out the cached array by reference meant the FIRST zoom on
 * any equalAspect chart silently corrupted the memoised "authored" extent to the zoomed
 * value for the rest of that data's lifetime — every later read (a level flip, a PNG
 * export, "Reset axes" itself) got the zoom back, not the original frame. Reproduced via
 * `ChartRenderer.plotly-integration.test.tsx`'s "reset still restores the authored frame
 * after a level flip while zoomed": clicking "Reset axes" after a zoom-then-level-flip
 * stayed at the zoomed range, because THIS function, called again on the same
 * `unfilteredData` reference to build that render's `layout.xaxis`, was already handing
 * back the corrupted value — nothing had zoomed `unfilteredData` itself.
 */
const extentCache = new WeakMap<DataPoint[], Map<string, [number, number] | undefined>>();

function cloneRange(r: [number, number] | undefined): [number, number] | undefined {
  return r ? [r[0], r[1]] : r;
}

export function dataExtentRange(rows: DataPoint[], key: string | undefined): [number, number] | undefined {
  if (!key) return undefined;
  let cached = extentCache.get(rows);
  if (cached?.has(key)) return cloneRange(cached.get(key));
  const values: number[] = [];
  for (const row of rows) {
    const n = toFiniteNumber(row[key]);
    if (n !== null) values.push(n);
  }
  const remember = (r: [number, number] | undefined) => {
    if (!cached) {
      cached = new Map();
      extentCache.set(rows, cached);
    }
    cached.set(key, r);
    return r;
  };
  if (values.length === 0) return remember(undefined);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  if (span <= 0) return remember(undefined);
  const pad = span * EQUAL_ASPECT_PAD;
  return cloneRange(remember([min - pad, max + pad]));
}

/**
 * Widest ENTRY the legend can ever draw, in px — every category label it may list,
 * counts included. Deliberately NOT the legend's title: see below.
 *
 * The count suffix is measured at its widest possible value (the whole dataset's row
 * count), never at the shown level's: the gutter has to fit every level and be the
 * same width for all of them, which is the entire point of sizing it here rather than
 * letting Plotly measure. Mirrors the label format in charts/spatialCategorical.
 */
function widestLegendText(model: NormalizedChartModel, scale: number): number {
  const { data, unfilteredData, encoding, labels, series, options } = model;
  const font = scaleFont(CHART_FONT, scale);
  const widths: number[] = [];

  // Deliberately NOT measured here: `labels.legendHeading`, whose text changes with the level
  // ("n = 96 bloques" against "n = 9 bloques"). Sizing the gutter to it would put the
  // geometry straight back under the control of the shown level. It is wrapped to the
  // resulting gutter instead, so it fits without having a say in the width.
  const rows = unfilteredData ?? data;
  const groupKey = encoding.groupKey;
  if (groupKey) {
    const suffix = options?.showLegendCounts ? ` — ${rows.length}` : '';
    for (const category of uniqueValues(rows, groupKey)) {
      widths.push(measureTextWidth(`${category}${suffix}`, font));
    }
  }
  for (const s of series) widths.push(measureTextWidth(s.name ?? s.dataKey, font));
  if (widths.length === 0 && labels.title) return 0;

  return widths.length > 0 ? Math.max(...widths) : 0;
}

/**
 * Whether the legend takes the figure's right gutter instead of sitting below the plot.
 *
 * The consumer's `legendOutside` is an explicit contract and always wins. Absent one the
 * composition follows the DEPOSIT'S SHAPE, for a reason that is pure geometry: under
 * `constrain: 'domain'` the drawing is INSCRIBED in the plot box preserving the data's
 * proportions, so it can only use `min(dataAspect, boxAspect) / max(…)` of that box — it
 * fills the box exactly when the two agree and loses ground as they diverge. The card's
 * height is uniform and fixed, so orienting the box like the data is the only lever left.
 *
 * Taller than wide (a N-S elongated deposit): the drawing is HEIGHT-bound, so every pixel
 * of width past `height / dataAspect` is waste. The legend takes that surplus, and the
 * plot gets back the height the legend was costing underneath it. Wider than tall: the
 * drawing is WIDTH-bound and wants the whole row, so the legend stays below, in the
 * vertical slack the drawing leaves over.
 *
 * Measured on the accuracy map before this — a 2.07 deposit in a 0.61 box — the drawing
 * was using 29% of its plot box and 12% of the card.
 *
 * Gated on `equalAspect`, the flag that means the proportions are the DATA's rather than
 * the layout's; anywhere else the box is free to be whatever shape the card is.
 *
 * Never where a stats box claims that gutter. The placement note further down is more
 * precise about which variants can share it — `statsRight` WITH columns does, without
 * them does not — but this predicate refuses both, because taking the gutter from a box
 * that has it means shrinking `xaxis.domain`, and a shrunk domain is what Plotly caches
 * into `_inputDomain` and later discards, drawing the box over the plot (see
 * `aspectConstraint`). A coarser gate than the geometry strictly needs, on purpose.
 */
export function legendTakesGutter(model: NormalizedChartModel, ctx: RenderContext): boolean {
  const options = model.options;
  if (options?.legendOutside !== undefined) return options.legendOutside;
  // Mirrors makeLayout's own resolution of these three, deliberately: a predicate that
  // disagreed with the layout it feeds would reserve a gutter nothing draws into.
  const square = options?.squareAxes ?? false;
  if (!((options?.equalAspect ?? false) && !square)) return false;
  const statsRight =
    (options?.showStatsBox ?? false) &&
    (options?.statsBoxRight ?? false) &&
    (ctx.hasStatistics ?? true);
  if (statsRight) return false;
  // Read from the WHOLE deposit, not the bench on show: the frame is ranged over
  // `unfilteredData` (US 2097), so reading the filtered rows would move the legend — and
  // with it the plot — every time the user scrubs a bench.
  // Length, not just presence: a dataset or config swap can leave `unfilteredData` an
  // EMPTY array for a frame while `data` still holds rows, and `??` hands that empty one
  // through — both extents read undecidable, the legend drops out of the gutter and
  // `margin.r` collapses with it, then both snap back. One frame of the composition
  // jumping, on every swap. An empty whole-deposit set carries no frame to read.
  const rows = model.unfilteredData?.length ? model.unfilteredData : model.data;
  const north = dataExtentRange(rows, model.encoding.yKey ?? 'y');
  const east = dataExtentRange(rows, model.encoding.xKey);
  // An ABSENT extent means the axis has no readable span, which is not the same fact as
  // "its span is zero": `dataExtentRange` returns undefined both for a column with no
  // finite values and for a degenerate one where every block shares a coordinate. Read
  // as zero, a deposit one column wide looked "taller than wide" and got a gutter
  // reserved beside an axis left on autorange — the frame jumping between benches that
  // US 2097 closed. Undecidable falls back to the composition that was there before.
  if (!north || !east) return false;
  return north[1] - north[0] > east[1] - east[0];
}

/**
 * Width in px of the right gutter the legend reserves when it takes it (see
 * `legendTakesGutter`); 0 for any other placement.
 *
 * Exported for the same reason as `resolveTitleBlock`: the legend's gutter is NOT the
 * authored `margin.r`. With `xref: 'container'` the legend reserves its own space
 * through Plotly's reserved-margin path, and a chart type's `buildLayout` may replace
 * the margin object outright (the renderer merges by top-level key). Anything sizing
 * itself against the space left over — the stats box — has to ask for the real number
 * rather than read a margin that may never have reached the layout.
 *
 * Sized to the WIDEST text the legend can ever show, not to the level on screen: with
 * the gutter fixed nothing widens it any more, so a longer label would simply be
 * clipped at the figure's edge. That worst case is stable — the category set comes from
 * the whole dataset and the count suffix is bounded by its row count — so the gutter
 * fits every level while never moving between them.
 */
export function resolveLegendGutter(model: NormalizedChartModel, ctx: RenderContext): number {
  if (!legendTakesGutter(model, ctx)) return 0;
  const scale = figureScale(ctx.figureWidth);
  const wanted = Math.max(
    scalePx(MARGIN.legendGutter, scale),
    widestLegendText(model, scale) + scalePx(LEGEND_GUTTER.entryChrome, scale)
  );
  // Capped HERE, not at the placement, so there is ONE number: what the legend asks for
  // and what it is given used to be two, and everything sizing itself against the gutter
  // — the placement, the heading's wrap, ChartRenderer's `rightInset` — read whichever
  // it happened to have. Measured: below ~430px of figure the cap bites, and a heading
  // wrapped to the uncapped width drew 12px past the figure's right edge.
  const figureWidth = ctx.figureWidth ?? 0;
  return figureWidth > 0 ? Math.min(wanted, figureWidth * LEGEND_GUTTER.maxFraction) : wanted;
}

/** Base layout shared by every chart type. */
export function makeLayout(
  model: NormalizedChartModel,
  ctx: RenderContext
): Record<string, unknown> {
  const { labels, encoding, series, data, options } = model;
  // Second axis is data-driven (type-agnostic): any series opting into the
  // right axis turns it on; with none, no `yaxis2` is emitted (single-axis
  // structure, as before the feature — the rest of the report-grade styling
  // below applies regardless).
  const hasRightAxis = series.some((s) => s.yAxis === 'right');
  // Y-axis only, by design — see ChartOptions.showYZeroLine and the xaxis.zeroline
  // comment below for why X never gets an equivalent opt-in.
  const yZeroline = options?.showYZeroLine ?? false;

  // Every in-figure size is expressed at TYPO_REFERENCE_WIDTH and scaled to the
  // measured container, so a card and a report figure are the same picture at two
  // zoom levels rather than two typographic layouts. See ../theme/scale.
  const title = resolveTitleBlock(model, ctx);
  const scale = title.scale;
  const axisLine = axisSpine(scale);
  // Breathing room above the data max so the topmost point isn't welded to the
  // plot's top border. Only applied when a legend is shown, matching the previous
  // behaviour: legend-less charts keep fitting the data tightly.
  //
  // A stats box drawn INSIDE the plot is a different matter: it is opaque and
  // anchored at the top, so it needs the old 20% to clear the peak data. The
  // reduced Y_HEADROOM is justified by the LEGEND having moved out of the plot,
  // which says nothing about that box.
  //
  // `hasStatistics` gates it for the same reason it gates `statsRight` below:
  // ChartRenderer draws the box only when `statistics` is non-empty, so without
  // this a chart with the flag on and no stats surrendered 20% of its vertical
  // scale to a box that never appeared.
  const insideStatsBox =
    (options?.showStatsBox ?? false) &&
    !(options?.statsBoxRight ?? false) &&
    (ctx.hasStatistics ?? true);
  const topHeadroom = insideStatsBox
    ? Y_HEADROOM_INSIDE_BOX
    : ctx.showLegend
      ? Y_HEADROOM
      : 0;
  // Square axes (scatter): X and Y share the origin (0) and a 1:1 scale. An
  // explicit [0, max] range on both axes pins the origin exactly at x=0/y=0 —
  // `rangemode:'tozero'` alone lets Plotly pad the low end and the Y axis drifts
  // off the zero. The headroom range is dropped (it fights the shared scale).
  const square = options?.squareAxes ?? false;
  // Spatial/plan view: fit both axes to the data (no forced 0 baseline) and lock
  // a 1:1 metre scale via yaxis.scaleanchor. Real-world coords (Este/Norte with
  // ~10^5 m offsets) would otherwise be crushed to a line by the [0,max] range.
  // …and `squareAxes` wins if both are set: the two are mutually exclusive, since the
  // scaleanchor equalAspect adds is exactly what pushes the pinned origin off 0 (see
  // above). Nothing configures both today, but `options` is reachable from WidgetEditor,
  // so resolve it here rather than emitting the broken combination.
  const equalAspect = (options?.equalAspect ?? false) && !square;
  // Legend in the right gutter (outside the plot) — set explicitly for dense plots where
  // an inside legend would cover data, and derived from the deposit's shape otherwise.
  // Needs a wide right margin for the entries. See `legendTakesGutter`.
  const legendOutside = legendTakesGutter(model, ctx);
  // Vertical gridlines, opt-in per chart — see ChartOptions.showVerticalGrid.
  const verticalGrid = options?.showVerticalGrid ?? false;
  // Gated on `showStatsBox`: `statsBoxRight` alone still shrinks the plot to
  // domain [0, 0.6], so a config where the box was hidden but the flag stayed set
  // gave up 40% of the plot to an empty gutter. Gating here (not in the editor)
  // also repairs dashboards already saved with that combination.
  const statsRight =
    (options?.showStatsBox ?? false) &&
    (options?.statsBoxRight ?? false) &&
    // …and only if a box will actually be drawn: ChartRenderer gates the annotation
    // on `statistics` being non-empty, so without this the plot shrank for a box
    // that never appeared.
    (ctx.hasStatistics ?? true);
  const statsColumns = options?.statsBoxColumns ?? false;
  // Limits live on the left axis (see resolveLimitsShapes) — include them so the
  // range never clips the very lines the chart exists to compare against.
  const limitValues = [
    model.limits?.upper,
    model.limits?.lower,
    ...(model.limits?.hlines ?? []).map((h) => h.value),
  ].filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  // The primary axis can opt out of the 0 floor too (`options.yFitData`),
  // mirroring `y2FitData` on the secondary axis. Unlike the secondary axis,
  // its bottom pad can't default to `topHeadroom` — that's 0 whenever the
  // chart shows no legend (see `topHeadroom` above), which is exactly the
  // shape of chart this flag targets, so a "default to topHeadroom" pad would
  // silently collapse to no margin at all. `Y_HEADROOM` is passed explicitly
  // instead, unconditionally; it is only read by `axisRangeWithHeadroom` when
  // `zeroFloor` is false, so this is a no-op for every chart leaving the flag off.
  // The top gets the same span-relative pad: without it the line at the max sits
  // on the border and Plotly clips half its stroke (Bug 2764).
  const leftZeroFloor = !(options?.yFitData ?? false);
  const leftRange =
    square || equalAspect
      ? null
      : axisRangeWithHeadroom(
          data,
          series,
          encoding,
          'left',
          topHeadroom,
          limitValues,
          leftZeroFloor,
          Y_HEADROOM,
          leftZeroFloor ? 0 : Y_HEADROOM
        );

  // Plan view: bound both axes to the extent of the WHOLE dataset, not of the rows
  // currently drawn. `filters` subset the data before the model is built (see
  // ../normalize), so autoranging here re-fit the frame to each bench and the map
  // jumped on every level change — US 2097. `unfilteredData` is present only when a
  // filter actually removed rows; without one the shown rows ARE the whole.
  //
  // Explicit ranges coexist with the scaleanchor below: Plotly's `constrain` defaults
  // to 'range', so enforce() widens the lower-scale axis symmetrically to reach 1:1 —
  // it never clips a requested range. Same request + same plot area ⇒ same frame for
  // every bench. (`constrain: 'domain'` would honour the ranges exactly and letterbox
  // the plot instead; that is the lever if a card is ever required to be
  // pixel-identical to the fixed-size report figure.)
  const aspectRows = equalAspect ? (model.unfilteredData ?? data) : [];
  const aspectXRange = equalAspect ? dataExtentRange(aspectRows, encoding.xKey) : undefined;
  const aspectYRange = equalAspect ? dataExtentRange(aspectRows, encoding.yKey ?? 'y') : undefined;

  // `constrain: 'domain'` satisfies the 1:1 scale by shrinking the drawing area rather
  // than widening the range, which is what pins the frame — but it cannot be used where
  // a stats box shares the figure. Constraining the domain makes Plotly cache the
  // axis' `_inputDomain`, a private key that survives every redraw
  // (plotly.js constraints.js:475-477 with lib/relink_private.js) and is only cleared
  // once the axis leaves the constraint group. The `[0, plotDomainEnd]` domain that
  // reserves the stats gutter is then silently discarded on any later render, the plot
  // reclaims the full width, and the box is drawn over it.
  //
  // With `constrain: 'range'` (the default) Plotly never takes that path, so the
  // reserved domain survives — at the price the branch below buys back: under 'range'
  // the drawn range is widened from the plot area in PIXELS, so the frame moves with
  // every change in available width. That is the trade a config still setting
  // `statsBoxRight` accepts; the plan views dropped the box precisely to escape it
  // (see charts/shared/levelView and config/chartIntent in conciliatron).
  const aspectConstraint = statsRight ? {} : { constrain: 'domain' };
  // `constraintoward` centres what the constraint SHRINKS — and only 'domain' shrinks
  // the drawing. Under the 'range' fallback above, the 1:1 is met by WIDENING the
  // requested range by an amount that tracks the plot area in pixels, and a centred
  // drawing slides with it on every resize and level change: the drift US 2097 closed
  // by pinning the frame to its top-left corner. So the pin goes where its reason is —
  // kept on the path that still falls back to 'range', dropped on the one that ships.
  const aspectToward = statsRight
    ? { x: 'left' as const, y: 'top' as const }
    : { x: 'center' as const, y: 'middle' as const };

  let squareRange: [number, number] | undefined;
  if (square) {
    const yk = encoding.yKey ?? 'y';
    const vals: number[] = [];
    for (const d of data) {
      if (encoding.xKey) {
        const x = Number(d[encoding.xKey]);
        if (Number.isFinite(x)) vals.push(x);
      }
      const y = Number(d[yk]);
      if (Number.isFinite(y)) vals.push(y);
    }
    // Small headroom so points at the max aren't clipped at the border.
    squareRange = [0, (vals.length ? Math.max(...vals) : 1) * 1.05];
  }

  // The legend's box is anchored to the container's BOTTOM edge, so at `y: 0` its
  // last row sits on the SVG viewport boundary and gets clipped. Lift it by a
  // constant number of px, expressed as the container fraction that many px come
  // to — 0 until the height is measured, same "don't guess" rule as everywhere else.
  const legendY =
    (ctx.figureHeight ?? 0) > 0 ? scalePx(LEGEND_PAD_BOTTOM, scale) / ctx.figureHeight! : 0;

  // Expressed as a container fraction, the unit that costs Plotly no text measurement.
  // Already capped by `resolveLegendGutter`, so this is a plain conversion — capping it
  // again here is what let the reserved width and the asked-for width diverge. Same
  // "don't guess until measured" rule as legendY above for the unmeasured case.
  const legendGutterPx = resolveLegendGutter(model, ctx);
  const legendGutterFraction =
    (ctx.figureWidth ?? 0) > 0
      ? legendGutterPx / ctx.figureWidth!
      : LEGEND_GUTTER.fallbackFraction;

  // Where the legend sits. Only two placements share the right gutter with something
  // else, and both fit above it: a legend truly outside the plot (`legendOutside`) and
  // the stats columns variant, whose per-model headers are short and whose columns
  // start lower down. `statsRight` WITHOUT columns is the case that doesn't fit — its
  // single stacked box needs the gutter's full height (wrapping makes it ~270px tall),
  // so its legend falls through to the default placement in the header block above the
  // plot. Pinning it to the gutter top instead overlapped the box by 148x123px on the
  // scatter.
  //
  // Both gutter placements also reset `yref` to 'paper': LEGEND_STYLE anchors to the
  // container bottom, where `y: 1` would mean the FIGURE top (over the title) rather
  // than the plot top.
  const legendPlacement: Record<string, unknown> = legendOutside
    ? {
        ...LEGEND_STYLE,
        font: scaleFont(CHART_FONT, scale),
        orientation: 'v',
        // `xref: 'container'` — NOT 'paper' — is what keeps the plot area from moving
        // when the legend's text changes: on 'paper' Plotly reserves the legend's own
        // measured width, so a per-level count of "— 65" against "— 6" shifted the
        // whole map. See LEGEND_GUTTER. And `x` must stay within [0, 1] here: out of
        // range Plotly silently falls back to x=1/right, drawing the legend ON TOP of
        // the plot.
        xref: 'container',
        x: 1 - legendGutterFraction,
        xanchor: 'left',
        // `yref` stays 'paper' — it anchors y=1 to the plot's top edge, and it is what
        // puts the legend on the reserved-margin path in the first place.
        y: 1,
        yanchor: 'top',
        yref: 'paper',
      }
    : statsRight && statsColumns
      ? {
          ...LEGEND_STYLE,
          font: scaleFont(CHART_FONT, scale),
          x: STATS_GUTTER.columnsStart,
          xanchor: 'left',
          y: 1,
          yanchor: 'top',
          yref: 'paper',
        }
      : // Centre on the DATA, not the paper: with the stats gutter the traces only
        // occupy `xaxis.domain`, so paper x 0.5 sits off to the right of what it labels.
        {
          ...LEGEND_STYLE,
          font: scaleFont(CHART_FONT, scale),
          x: statsRight ? STATS_GUTTER.plotDomainEnd / 2 : LEGEND_STYLE.x,
          y: legendY,
        };

  // The plan view's per-bench readouts head the entries they are computed from (see
  // shared/levelView). Plotly draws a legend title above the entries, which is exactly
  // that relationship — and it costs no plot area, unlike a stats box's gutter.
  if (labels.legendHeading) {
    const headingFont = scaleFont(CHART_FONT, scale);
    // WRAPPED to the gutter rather than allowed to widen it: the heading's text changes
    // with the level ("766 bloques" against "525 bloques"), so sizing the gutter to it
    // would hand the geometry back to the shown level — the same reason
    // `widestLegendText` leaves it out, and the same treatment.
    //
    // Wrapped against what the gutter RESERVES (`legendGutterPx`, capped inside
    // `resolveLegendGutter`), minus the per-entry chrome Plotly draws to the left of
    // every label. The heading carries no swatch, so this is conservative rather than
    // exact — the alternative, measuring the title's own left inset, is not exposed.
    // Below the plot the heading has the figure's whole width, and Plotly does not wrap
    // a legend title, so it is passed through untouched.
    //
    // Zero width means the figure has not been measured yet: `wrapToWidth` returns the
    // text unwrapped, the same "don't guess before measuring" contract the title fit
    // takes. The heading may clip for that one frame; the observer's redraw wraps it.
    const measured = (ctx.figureWidth ?? 0) > 0;
    const chrome = scalePx(LEGEND_GUTTER.entryChrome, scale);
    // Subtracting the chrome is conservative — the heading carries no swatch — but on a
    // figure narrow enough that the CAPPED gutter (40% of it) is thinner than the chrome
    // itself, the subtraction goes negative and `wrapToWidth`'s `<= 0` guard hands back
    // the text UNWRAPPED: the very overflow the cap exists to prevent, reappearing at the
    // narrow end of the cap's own range. Below ~115px of figure that is every heading.
    // Fall back to the whole gutter there — still inside the figure, which is the bound
    // that actually matters.
    const gutterRoom = legendGutterPx - chrome > 0 ? legendGutterPx - chrome : legendGutterPx;
    // Below the plot the heading has the figure's whole width rather than the gutter's —
    // but it is not unbounded. Plotly does not wrap a legend title at all, so a long
    // heading (or any future chart adopting `legendHeading`) runs off the figure unless
    // it is wrapped here, exactly like every other text in this file.
    const headingRoom = legendOutside ? gutterRoom : (ctx.figureWidth ?? 0) - chrome;
    legendPlacement.title = {
      text: measured
        ? wrapToWidth(labels.legendHeading, headingRoom, headingFont)
        : labels.legendHeading,
      font: headingFont,
      side: 'top' as const,
    };
  }



  const layout: Record<string, unknown> = {
    // Report-grade defaults: Arial/black type, white canvas, soft horizontal-only
    // grid, black axis spines, legend below the plot. Title lives in the figure
    // (automargin) so it travels in PNG exports. Tokens live in ../theme/layout.
    font: scaleFont(CHART_FONT, scale),
    // Chrome, so it scales with the figure like the ticks — see HOVER_FONT for why
    // Plotly's default is the one type that would not.
    hoverlabel: { font: scaleFont(HOVER_FONT, scale) },
    title: {
      text: title.text,
      ...(title.subtitle
        ? { subtitle: { text: title.subtitle, font: scaleFont(SUBTITLE_FONT, scale) } }
        : {}),
      // Flush with the plot area's left edge, not centred on the paper: the title
      // and subtitle read as the CARD'S HEADER, sharing a left margin with the Y
      // axis below them, instead of floating as a caption over the figure.
      // `xref: 'paper'` is what puts x=0 at the plot's left edge — against the
      // default 'container' it would be the figure's edge, out in the margin.
      x: 0,
      xref: 'paper',
      xanchor: 'left',
      automargin: true,
      // Keeps the glyph ascenders off the figure's top edge — `automargin`
      // anchors the title to the container, where Plotly's default pad of 0
      // would clip them. See TITLE_PAD_TOP.
      pad: { t: title.padTop },
      font: scaleFont(TITLE_FONT, scale),
    },
    paper_bgcolor: SURFACE_COLOR,
    plot_bgcolor: SURFACE_COLOR,
    // Scaled with the type: a 1.54x figure whose left margin stayed at 60px would
    // clip the tick labels the same scaling just enlarged.
    margin: {
      t: scalePx(MARGIN.base.t, scale),
      l: scalePx(MARGIN.base.l, scale),
      b: scalePx(MARGIN.base.b, scale),
      // The gutter, not the constant it starts from: `resolveLegendGutter` sizes it to
      // the WIDEST entry the legend can ever show and can reach 40% of the figure, well
      // past `MARGIN.legendGutter`. Authoring the constant here made this the one reader
      // of the gutter that disagreed with `resolveLegendGutter` — the single number the
      // placement, the heading's wrap and ChartRenderer's `rightInset` all take — and a
      // margin narrower than the space the legend occupies is a drawing that runs under
      // it. Already scaled inside, so no `scalePx` on this branch.
      r: legendOutside
        ? legendGutterPx
        : scalePx(hasRightAxis ? MARGIN.rightAxis : MARGIN.base.r, scale),
    },
    showlegend: ctx.showLegend,
    // With the stats box on the right, the plot is shrunk (xaxis.domain) and both the
    // legend and the box live in the freed space. In side-by-side `columns` mode the
    // per-entry columns sit below a horizontal legend across the gutter top;
    // otherwise the single stacked box sits below a vertical legend (which stacks in
    // the narrow gutter instead of spreading into the box). Resolved above.
    // Bug 2720: `fitMarkerToCell` can shrink the blocks to a few px, and Plotly draws
    // each legend swatch at its trace's marker size — the class key would shrink with
    // them. A constant swatch keeps the key readable however dense the grid is.
    legend: options?.fitMarkerToCell && equalAspect ? { ...legendPlacement, itemsizing: 'constant' } : legendPlacement,
    // Vertical grid off by default (report style) unless `showVerticalGrid` opts in,
    // in which case it mirrors ctx.showGrid like the horizontal grid does — an actual
    // grid, not just horizontal lines. Bold axis titles, kept off the tick values via
    // standoff; `automargin` grows the reserved space so the title is never clipped
    // at the figure edge.
    xaxis: {
      title: axisTitle(labels.xAxisLabel ?? encoding.xKey ?? '', scale),
      automargin: true,
      showgrid: verticalGrid ? ctx.showGrid : false,
      gridcolor: GRID_COLOR,
      gridwidth: GRID_WIDTH,
      // Off unconditionally, for every chart — deliberately, not just Plotly's
      // default. Report style draws no vertical line at x=0 at all: the X axis
      // here is as often a category (period, block, cutoff grade) as a numeric
      // one, where a "zero" reference reads as noise rather than a baseline.
      // `showYZeroLine` (below, on yaxis) is Y-only for the same reason it exists
      // in the first place — an error/deviation chart's 0 is a meaningful Y
      // reference; there is no equivalent X case among today's chart types, so
      // there is no `showXZeroLine`. Before this was unconditional, Plotly's own
      // default drew a bare, unstyled vertical line at x=0 whenever 0 fell inside
      // the range, disconnected from the grid on/off toggle above.
      zeroline: false,
      ...(options?.xAxisCategorical ? { type: 'category' } : {}),
      // Plan view: plain grouped numbers on X too (e.g. 551,790 not "551.4k"),
      // matching the Y axis — real coordinates must not read as SI/scientific.
      ...(equalAspect ? NUMBER_TICKS : {}),
      // Constrained on both axes — see the yaxis note below for why 'domain', and
      // for why the drawing is anchored rather than centred.
      ...(aspectXRange
        ? { range: aspectXRange, ...aspectConstraint, constraintoward: aspectToward.x }
        : {}),
      ...(squareRange ? { range: squareRange } : {}),
      // Shrink the plot to the left so the stats box sits in the free space right.
      ...(statsRight ? { domain: [0, STATS_GUTTER.plotDomainEnd] } : {}),
      ...axisLine,
    },
    yaxis: {
      title: axisTitle(labels.yAxisLabel ?? encoding.yKey ?? '', scale),
      automargin: true,
      showgrid: ctx.showGrid,
      gridcolor: GRID_COLOR,
      gridwidth: GRID_WIDTH,
      ...NUMBER_TICKS,
      zeroline: yZeroline,
      zerolinecolor: AXIS_LINE_COLOR,
      // Plan view: lock 1:1 with X so a metre east equals a metre north (blocks
      // stay square), over the explicit whole-dataset ranges resolved above.
      ...(equalAspect ? { scaleanchor: 'x', scaleratio: 1 } : {}),
      // `constrain: 'domain'` (Plotly's default is 'range') is what makes the frame
      // truly identical across levels. Under 'range' the 1:1 constraint is satisfied by
      // WIDENING the requested range, by an amount that depends on the plot area in
      // pixels — and that area moves with anything automargin measures: the legend's
      // entry count, the width of the "— 65" vs "— 6" counts in its labels, the
      // wrapped height of the subtitle. Every one of those changes with the shown
      // level, so the axes kept drifting even after the category set was fixed.
      // Constraining the DOMAIN instead honours the range exactly and shrinks the
      // drawing area to keep metres square, leaving a margin where the aspect doesn't
      // fill the card. A stable frame is the point of a plan view — the map must be
      // comparable bench to bench — so that margin is the right thing to spend.
      //
      // `constraintoward` CENTRES what it shrinks, on both axes. Which side has room to
      // spare IS readable from the data — `legendTakesGutter` reads exactly that — but it
      // is the wrong lever here: which end of the surplus the drawing sits at is a
      // composition choice, and centred is the only one that reads right whether the
      // leftover lands at the sides or above and below.
      //
      // It used to be pinned to the top-left, as a defence: under `constrain: 'range'`
      // the plot area moved with everything automargin measured, and a centred drawing
      // slid sideways with it. That instability leaves with the stats box — the gutter
      // that forces 'range' in the first place — which conciliatron's plan views have
      // dropped. A config that still sets `statsBoxRight` keeps the 'range' fallback, so
      // it keeps the pin too: see `aspectToward`, which is why this is not a constant.
      ...(aspectYRange
        ? { range: aspectYRange, ...aspectConstraint, constraintoward: aspectToward.y }
        : {}),
      ...(leftRange ? { range: leftRange } : {}),
      // Explicit [0,max] range on both axes pins the origin at (0,0) and shares
      // the data scale. No `scaleanchor`: on a non-square plot area (e.g. the
      // shrunk domain when the stats box is on the right) the anchor expands one
      // axis to force a 1:1 pixel ratio, which pushes the origin off 0.
      ...(squareRange ? { range: squareRange } : {}),
      ...axisLine,
    },
    autosize: true,
  };

  if (hasRightAxis) {
    // The secondary axis has its own opt-out of the 0 floor (`options.y2FitData`),
    // independent of the primary axis's `options.yFitData` resolved above (`leftZeroFloor`)
    // — a dual-axis chart can fit either axis, both, or neither to its data.
    const rightZeroFloor = !(options?.y2FitData ?? false);
    const rightRange = axisRangeWithHeadroom(
      data,
      series,
      encoding,
      'right',
      topHeadroom,
      [],
      rightZeroFloor,
      Y2_FIT_DATA_BOTTOM_HEADROOM
    );
    layout.yaxis2 = {
      overlaying: 'y',
      side: 'right',
      title: axisTitle(labels.y2AxisLabel ?? '', scale),
      automargin: true,
      showgrid: false,
      ...NUMBER_TICKS,
      ...(rightRange ? { range: rightRange } : {}),
      ...axisLine,
    };
  }

  return layout;
}

/** Y-axis id a series in `panel` (0-based) draws on: panel 0 → 'y', 1 → 'y2', … */
export function panelAxisId(panel: number): string {
  return panel === 0 ? 'y' : `y${panel + 1}`;
}

/** Vertical gap between stacked panels, as a fraction of the plot height. */
const PANEL_GAP = 0.08;

/**
 * Layout for the `stackedPanels` chart: one shared X axis at the bottom and N
 * vertically stacked Y axes (panel 0 on top), each with its own `domain` and
 * title. Per-panel Y ranges are left to Plotly autorange — every panel scales
 * to its own data, which is the whole point of stacking measures with different
 * units. Returned as a `buildLayout` override that fully replaces the base
 * single-axis `xaxis`/`yaxis` (the renderer shallow-merges by top-level key).
 *
 * Panels are 0-based, top to bottom; use a contiguous range starting at 0.
 * Panel 0 maps to the base `yaxis` key, so the override always replaces it —
 * if no series used panel 0, the base `yaxis` would survive as an orphan axis.
 */
export function stackedPanelsLayout(
  model: NormalizedChartModel,
  ctx: RenderContext
): Record<string, unknown> {
  const { series, labels, options } = model;
  const panelLabels = options?.panels ?? [];
  // Same proportional sizing as makeLayout — these panels replace the base axes
  // wholesale, so they must scale with the figure too or a stacked-panel chart
  // would be the one type whose type stayed absolute. See ../theme/scale.
  const scale = figureScale(ctx.figureWidth);
  // Distinct panels in ascending order (top = lowest index). With no series
  // (degenerate, but reachable: data present, series empty) fall back to a
  // single panel so the X axis still anchors to a real Y axis ('y') instead
  // of 'yNaN'.
  const distinct = [...new Set(series.map((s) => s.panel ?? 0))].sort((a, b) => a - b);
  const panels = distinct.length ? distinct : [0];
  // Panel 0 maps to the base `yaxis`, so the override must include it — else the
  // base single-axis `yaxis` survives as an orphan overlapping all panels. Config
  // is hand-authored, so this is a dev-time warning, not a runtime normalization.
  if (import.meta.env.DEV && !panels.includes(0)) {
    console.warn(
      '[dashboard-kit] stackedPanels: no series uses panel 0; the base Y axis ' +
        'will render as an orphan. Start panel indices at 0.'
    );
  }
  const n = panels.length;
  const height = (1 - (n - 1) * PANEL_GAP) / n;

  // Round to kill floating-point dust so the bottom panel lands exactly on 0
  // and the top on 1 (cleaner domains, exact endpoints for callers/tests).
  const round = (v: number) => Math.round(v * 1e6) / 1e6;

  const layout: Record<string, unknown> = {};
  panels.forEach((panel, pos) => {
    const top = 1 - pos * (height + PANEL_GAP);
    const bottom = top - height;
    const key = panel === 0 ? 'yaxis' : `yaxis${panel + 1}`;
    layout[key] = {
      domain: [round(Math.max(0, bottom)), round(top)],
      anchor: 'x',
      title: axisTitle(panelLabels[panel]?.label ?? '', scale),
      automargin: true,
      showgrid: ctx.showGrid,
      gridcolor: GRID_COLOR,
      gridwidth: GRID_WIDTH,
      ...NUMBER_TICKS,
      ...axisSpine(scale),
    };
  });

  // Single X axis, drawn once at the bottom panel (all panels share it).
  layout.xaxis = {
    title: axisTitle(labels.xAxisLabel ?? '', scale),
    automargin: true,
    showgrid: false,
    anchor: panelAxisId(panels[panels.length - 1]),
    // Discrete category X: every value gets its own tick (see makeLayout).
    ...(options?.xAxisCategorical ? { type: 'category' } : {}),
    ...axisSpine(scale),
  };

  // Panels run on pure autorange (no headroom), so the top panel's line can
  // reach the plot's top edge where the base inside top-right legend sits. Put
  // the legend centered below the shared X axis (horizontal) so it never
  // overlaps a panel and doesn't steal horizontal width from the plots; the
  // extra bottom margin leaves room for it under the X-axis title.
  layout.legend = {
    bgcolor: LEGEND_STYLE.bgcolor,
    bordercolor: LEGEND_STYLE.bordercolor,
    borderwidth: LEGEND_STYLE.borderwidth,
    font: scaleFont(CHART_FONT, scale),
    orientation: 'h',
    x: 0.5,
    xanchor: 'center',
    y: -0.2,
    yanchor: 'top',
  };
  layout.margin = {
    t: scalePx(MARGIN.base.t, scale),
    r: scalePx(MARGIN.base.r, scale),
    // Wider left / taller bottom than the base: N stacked Y-axis titles and the
    // legend under the shared X axis both need their own room.
    l: scalePx(72, scale),
    b: scalePx(100, scale),
  };
  return layout;
}

/**
 * Turn `limits` into Plotly shapes: an optional band, horizontal upper/lower
 * lines, and optional vertical reference lines. Colors come from the semantic
 * palette unless overridden per-limit.
 */
export function resolveLimitsShapes(lim: ChartLimits, scale = 1): ChartTrace[] {
  const upperVal = typeof lim.upper === 'number' ? lim.upper : undefined;
  const lowerVal = typeof lim.lower === 'number' ? lim.lower : undefined;
  const shapes: ChartTrace[] = [];
  // Stroke widths follow the figure like every other size: a 2px limit line on a
  // 1000px report figure is proportionally half of what it is on the card.
  const width = REFERENCE_LINE_WIDTH * scale;
  const line = (value: number, axis: 'x' | 'y', color: string) =>
    referenceLineShape({ value, axis, color, width });

  if (upperVal !== undefined && lowerVal !== undefined) {
    shapes.push({
      type: 'rect',
      // Span the plot's x domain, not the full paper, so the band stays inside
      // the plot when it's shrunk for a right-side stats box (see referenceLineShape).
      xref: 'x domain',
      x0: 0,
      x1: 1,
      y0: lowerVal,
      y1: upperVal,
      fillcolor: lim.upperColor || lim.lowerColor || SEMANTIC.limitBand,
      opacity: LIMIT_BAND_OPACITY,
      line: { width: 0 },
    });
  }
  if (upperVal !== undefined) {
    shapes.push(
      line(upperVal, 'y', lim.upperColor || SEMANTIC.limitUpper)
    );
  }
  if (lowerVal !== undefined) {
    shapes.push(
      line(lowerVal, 'y', lim.lowerColor || SEMANTIC.limitLower)
    );
  }
  for (const v of lim.vlines ?? []) {
    shapes.push(
      line(v.value, 'x', v.color || SEMANTIC.reference)
    );
  }
  for (const h of lim.hlines ?? []) {
    shapes.push(
      line(h.value, 'y', h.color || SEMANTIC.reference)
    );
  }
  return shapes;
}
